<template>
  <v-container fluid>
    <v-alert v-if="error" type="error">{{ error }}</v-alert>

    <div class="d-flex flex-wrap align-center">
      <div class="text-h6 mr-3">Customer activity</div>
      <router-link :to="{name: 'Customer', params: {id: uid}}" class="caption">{{ uid }}</router-link>
      <v-spacer/>
      <v-select
        v-model="selectedDays"
        :items="daysOptions"
        label="Show last"
        dense
        outlined
        hide-details
        class="mr-3 mt-2"
        style="max-width: 150px;"
        @change="fetchTrackingData"
      />
      <v-select
        v-model="selectedChanges"
        :items="changeOptions"
        label="Changes"
        placeholder="All"
        persistent-placeholder
        dense
        outlined
        multiple
        clearable
        hide-details
        class="mr-3 mt-2"
        style="min-width: 220px; max-width: 360px;"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index < 2" x-small close @click:close="removeChangeFilter(item.value)">{{ item.value }}</v-chip>
          <span v-else-if="index === 2" class="caption text--secondary">+{{ selectedChanges.length - 2 }} more</span>
        </template>
      </v-select>
      <v-switch v-model="changesOnly" label="Only visits with changes" dense hide-details class="mr-3 mt-2"/>
      <v-switch v-model="showApiCalls" label="Show API calls" dense hide-details class="mr-3 mt-2"/>
      <v-btn
        v-if="mealWeeks.length"
        @click="loadMealNames"
        :loading="loadingMeals"
        :disabled="!unloadedMealWeeks.length"
        outlined
        class="mr-3 mt-2"
      >
        <v-icon left>mdi-silverware-fork-knife</v-icon>
        {{ unloadedMealWeeks.length ? `Load meal names (${plural(unloadedMealWeeks.length, 'week')} of menus)` : 'Meal names loaded' }}
      </v-btn>
      <v-btn @click="fetchTrackingData" :loading="loading" color="primary" class="mt-2">
        <v-icon left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </div>

    <div v-if="trackingData" class="text--secondary mt-2 mb-2">
      <span v-if="selectedChanges.length">{{ plural(shownVisitCount, 'visit') }} with {{ selectedChanges.join(' or ') }};</span>
      {{ visits.length }} visits, {{ visitsWithChanges }} with changes,
      from {{ trackingData.events.length.toLocaleString() }} events in the last {{ selectedDays }} days
      ({{ trackingData.totalEvents.toLocaleString() }} recorded in total).
      A visit is one device's activity without a {{ idleGapMinutes }} minute break.
      <v-chip v-if="trackingData.hasOlderEvents" small class="ml-1">
        <v-icon left small>mdi-clock-outline</v-icon>
        Older events available
      </v-chip>
    </div>

    <v-row v-if="trackingData">
      <v-col cols="12" md="9">
        <div v-if="!shownDays.length" class="text-center pa-4">
          <v-icon large color="grey">mdi-database-search</v-icon>
          <p class="mt-2">No {{ selectedChanges.length || changesOnly ? 'visits with those changes' : 'tracking events' }} in this period</p>
        </div>

        <div v-for="day in shownDays" :key="day.key" class="mb-4">
          <div class="subtitle-1 font-weight-medium mb-1">{{ day.label }}</div>
          <v-expansion-panels multiple accordion>
            <v-expansion-panel v-for="visit in day.visits" :key="visit.id" :class="{'visit-with-changes': visit.hasChanges}">
              <v-expansion-panel-header class="py-2">
                <div>
                  <div class="d-flex flex-wrap align-center">
                    <span class="font-weight-medium mr-3">{{ formatTime(visit.start) }} – {{ formatTime(visit.end) }}</span>
                    <span class="text--secondary mr-3">{{ formatDuration(visit.end - visit.start) }}</span>
                    <v-icon small class="mr-1">{{ visit.device.icon }}</v-icon>
                    <span class="mr-3">{{ visit.device.label }}</span>
                    <span class="text--secondary">{{ visit.site }}</span>
                  </div>
                  <div class="mt-1">
                    <v-chip v-for="change in visit.changeSummary" :key="change.label" x-small color="primary" class="mr-1 mb-1">
                      {{ change.label }}<span v-if="change.count > 1">&nbsp;×{{ change.count }}</span>
                    </v-chip>
                    <span class="caption text--secondary">
                      {{ plural(visit.pageCount, 'page') }} · {{ plural(visit.apiCount, 'API call') }}
                    </span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="caption text--secondary mb-2">
                  IP {{ visit.ips.join(', ') }}
                  <span v-if="visit.client"> · {{ visit.client }}</span>
                  <div>{{ visit.userAgent }}</div>
                </div>
                <div v-for="row in visitRows(visit)" :key="row.key" class="d-flex timeline-row" :class="'timeline-' + row.kind">
                  <div class="timeline-time">{{ formatTime(row.time, true) }}</div>
                  <v-icon small class="mx-2" :color="row.kind === 'change' ? 'primary' : undefined">{{ kindIcons[row.kind] }}</v-icon>
                  <div>
                    <span class="timeline-title">{{ row.title }}</span>
                    <span v-if="row.count > 1" class="text--secondary"> ×{{ row.count }}</span>
                    <div v-for="line in row.lines" :key="line.id" class="timeline-detail">
                      <span v-if="line.text">{{ line.text }}</span>
                      <span v-for="arg in line.args" :key="arg.key" class="timeline-arg">
                        <span class="timeline-arg-key">{{ arg.key }}</span>
                        <span class="timeline-arg-value">{{ arg.value }}</span>
                        <router-link
                          v-if="arg.mealId && mealsById[arg.mealId]"
                          :to="mealLink(arg.mealId)"
                          class="ml-1"
                        >{{ mealsById[arg.mealId].name }}</router-link>
                      </span>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-col>

      <v-col cols="12" md="3" v-if="trackingData.userInfo">
        <v-card outlined>
          <v-card-subtitle class="pb-2"><strong>Latest device</strong></v-card-subtitle>
          <v-card-text class="pt-0 caption">
            <div v-for="(value, key) in getUserProperties(trackingData.userInfo)" :key="key">
              <strong>{{ formatPropHeader(key) }}:</strong> {{ key === 'user-agent' ? formatUserAgent(value).label : value }}
              <span v-if="getLastChanged(key)" class="text--secondary"> (changed {{ formatTimestamp(getLastChanged(key)) }})</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
  </v-container>
</template>

<script>
import api from '../api'
import moment from 'moment'

const IDLE_GAP_MINUTES = 30
// a customer's change and the api call it makes land within this window
const DUPLICATE_WINDOW_MS = 10 * 1000

// named events that are not the customer changing anything
const INFO_EVENTS = new Set([
  'login', 'has-invoice', 'legacy-box-redirect', 'verify already verified', 'meal_view_opened', 'signup-check'
])

// api calls that change something (by lower-cased function name); every other
// api call is a page load or read. The first five repeat a named event.
const API_CHANGES = {
  setquantity: 'set quantity',
  setboxorder: 'set box order',
  updateuser: 'update user',
  pausedeliveries: 'pause deliveries',
  closeaccount: 'close account',
  confirmorder: 'confirm order',
  finishregistration: 'finish registration',
  registeruser: 'register user',
  referfriend: 'refer a friend',
  retrypayment: 'retry payment',
  savefeedback: 'save feedback',
  savewebappfeedback: 'save app feedback',
  saveswapresponse: 'save swap response',
  autopopulatebox: 'auto-fill box',
  requestpasswordreset: 'request password reset',
  generateemailconfirmation: 'send email confirmation',
}

// the meal a quantity change is for: chef's choice orders carry it as `id`
function mealIdOf(props) {
  if (props.meal_id) return props.meal_id
  if (props.order_type === 'chefs_choice' && props.id) return props.id
  return null
}

function toMillis(timestamp) {
  if (!timestamp) return 0
  const seconds = timestamp._seconds ?? timestamp.seconds
  if (seconds !== undefined) return seconds * 1000
  return new Date(timestamp).getTime()
}

function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return 'none'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : 'none'
  if (typeof value === 'object') {
    const parts = Object.entries(value)
      .filter(([, v]) => !isEmpty(v))
      .map(([k, v]) => `${k}: ${formatValue(v)}`)
    return parts.length ? parts.join('; ') : 'none'
  }
  return String(value)
}

function isEmpty(value) {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.values(value).every(isEmpty)
  return false
}

function classify(event) {
  const name = event.event || ''
  const apiMatch = name.match(/^api[-_]3-(.+)$/)
  if (apiMatch) {
    const functionName = apiMatch[1].toLowerCase()
    const changeLabel = API_CHANGES[functionName]
    if (changeLabel) return {kind: 'change', label: changeLabel, fromApi: true}
    return {kind: 'api', label: functionName}
  }
  if (name === 'pageview') return {kind: 'page', label: 'page'}
  if (INFO_EVENTS.has(name)) return {kind: 'info', label: name}
  return {kind: 'change', label: name}
}

export default {
  name: 'CustomerTracking',
  props: {
    uid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingMeals: false,
      // meal id → {name, date}, from the menus of the weeks the events mention; loaded on request
      mealsById: {},
      loadedMealWeeks: [],
      error: null,
      trackingData: null,
      changesOnly: false,
      selectedChanges: [],
      showApiCalls: false,
      idleGapMinutes: IDLE_GAP_MINUTES,
      selectedDays: 7,
      daysOptions: [
        {text: '1 day', value: 1},
        {text: '3 days', value: 3},
        {text: '1 week', value: 7},
        {text: '2 weeks', value: 14},
        {text: '1 month', value: 30},
        {text: '3 months', value: 90},
        {text: '6 months', value: 180},
        {text: '1 year', value: 365}
      ],
      kindIcons: {
        change: 'mdi-pencil',
        page: 'mdi-file-document-outline',
        info: 'mdi-information-outline',
        api: 'mdi-swap-horizontal',
      },
    }
  },
  mounted() {
    this.fetchTrackingData()
  },
  watch: {
    uid() {
      this.fetchTrackingData()
    }
  },
  computed: {
    // events split into visits: one device + site, until it goes quiet for IDLE_GAP_MINUTES
    visits() {
      if (!this.trackingData?.events) return []
      const events = this.trackingData.events
        .map(event => ({...event, time: toMillis(event.timestamp), ...classify(event)}))
        .sort((a, b) => a.time - b.time)

      const openVisits = {}
      const visits = []
      for (const event of events) {
        const visitKey = `${event['user-agent']}|${event.origin}`
        let visit = openVisits[visitKey]
        if (!visit || event.time - visit.end > IDLE_GAP_MINUTES * 60 * 1000) {
          visit = {id: event.id, userAgent: event['user-agent'], origin: event.origin, start: event.time, end: event.time, events: []}
          openVisits[visitKey] = visit
          visits.push(visit)
        }
        visit.end = event.time
        visit.events.push(event)
      }
      return visits.map(visit => this.summariseVisit(visit)).reverse()
    },

    visitsWithChanges() {
      return this.visits.filter(visit => visit.hasChanges).length
    },

    // the kinds of change in the loaded events, most frequent first
    changeOptions() {
      const counts = {}
      this.visits.forEach(visit => visit.changeSummary.forEach(change => {
        counts[change.label] = (counts[change.label] || 0) + change.count
      }))
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({text: `${label} (${count})`, value: label}))
    },

    // the menu weeks holding the meals the events mention
    mealWeeks() {
      const weeks = new Set()
      ;(this.trackingData?.events || []).forEach(event => {
        const props = event.props || {}
        const date = props.date || props.deliveryDate
        if (mealIdOf(props) && date) weeks.add(moment(date).startOf('week').format('YYYY-MM-DD'))
      })
      return [...weeks].sort()
    },

    unloadedMealWeeks() {
      return this.mealWeeks.filter(week => !this.loadedMealWeeks.includes(week))
    },

    shownVisitCount() {
      return this.shownDays.reduce((total, day) => total + day.visits.length, 0)
    },

    shownDays() {
      const days = []
      for (const visit of this.visits) {
        if (this.changesOnly && !visit.hasChanges) continue
        if (this.selectedChanges.length && !visit.changeSummary.some(change => this.selectedChanges.includes(change.label))) continue
        const key = moment(visit.start).format('YYYY-MM-DD')
        let day = days[days.length - 1]
        if (!day || day.key !== key) {
          day = {key, label: moment(visit.start).format('dddd, MMMM D YYYY'), visits: []}
          days.push(day)
        }
        day.visits.push(visit)
      }
      return days
    },
  },
  methods: {
    async fetchTrackingData() {
      this.loading = true
      this.error = null

      try {
        const response = await api.get(`/v2/tracking/${this.uid}?days=${this.selectedDays}`)
        this.trackingData = response.data
      } catch (error) {
        console.error('Error fetching tracking data:', error)
        this.error = 'Failed to load tracking data'
      } finally {
        this.loading = false
      }
    },

    // a week's menu is a couple of MB, so fetch a few at a time and keep only the names
    async loadMealNames() {
      this.loadingMeals = true
      const mealsById = {...this.mealsById}
      const weeks = [...this.unloadedMealWeeks]
      try {
        while (weeks.length) {
          const batch = weeks.splice(0, 4)
          const menus = await Promise.all(batch.map(week => api.get(`v2/menu/${week}`).then(({data}) => data)))
          menus.forEach(menu => {
            Object.values(menu || {}).forEach(streamMenu => (streamMenu?.menu || []).forEach(day => {
              (day.meals || []).forEach(meal => {
                mealsById[meal.id] = {name: meal.name, date: meal.date || day.date}
              })
              // chef's choice meals are offered for the whole week, as products of the entry dated 'any'
              // with ids like '10191CC'
              ;(day.products || []).forEach(product => {
                const id = String(product.id).replace(/CC$/i, '')
                if (!mealsById[id]) mealsById[id] = {name: product.name, date: null}
              })
            }))
          })
          this.loadedMealWeeks = [...this.loadedMealWeeks, ...batch]
          this.mealsById = {...mealsById}
        }
      } catch (error) {
        console.error('Error loading menus:', error)
        this.error = 'Failed to load the menus for meal names'
      } finally {
        this.loadingMeals = false
      }
    },

    // a chef's choice meal has no day, so no MealDetail page
    mealLink(mealId) {
      const date = this.mealsById[mealId].date
      if (date) return {name: 'MealDetail', params: {date, id: mealId}}
      return {name: 'MealDesign', params: {id: mealId}}
    },

    removeChangeFilter(label) {
      this.selectedChanges = this.selectedChanges.filter(selected => selected !== label)
    },

    summariseVisit(visit) {
      const events = visit.events.filter(event => !this.isDuplicateApiChange(event, visit.events))
      const changes = events.filter(event => event.kind === 'change')

      const changeCounts = {}
      changes.forEach(event => {
        changeCounts[event.label] = (changeCounts[event.label] || 0) + 1
      })

      const app = events.find(event => event.props?.app)?.props.app
      const host = (visit.origin || '').replace(/^https?:\/\//, '')
      const loginDetails = [...events].reverse().find(event => event.event === 'login' && event.props?.buildVersion)?.props

      return {
        ...visit,
        events,
        hasChanges: changes.length > 0,
        changeSummary: Object.entries(changeCounts).map(([label, count]) => ({label: capitalise(label), count})),
        pageCount: events.filter(event => event.kind === 'page').length,
        apiCount: events.filter(event => event.kind === 'api' || event.fromApi).length,
        device: this.formatUserAgent(visit.userAgent),
        site: app ? `${host} (${app})` : host,
        ips: [...new Set(events.map(event => event.ip).filter(Boolean))],
        client: loginDetails && this.formatClient(loginDetails),
      }
    },

    // a change api call right next to the named event for the same change adds nothing
    isDuplicateApiChange(event, visitEvents) {
      if (!event.fromApi) return false
      return visitEvents.some(other => !other.fromApi
        && other.label === event.label
        && Math.abs(other.time - event.time) <= DUPLICATE_WINDOW_MS)
    },

    // the visit's events as timeline rows: a run of page views becomes one row, and
    // a run of the same change one row with a line per change
    visitRows(visit) {
      const rows = []
      for (const event of visit.events) {
        if (event.kind === 'api' && !this.showApiCalls) continue
        const title = this.eventTitle(event)
        if (this.selectedChanges.length && !(event.kind === 'change' && this.selectedChanges.includes(title))) continue
        const detail = this.eventDetail(event)
        const previous = rows[rows.length - 1]
        // while filtering, the rows between two changes are hidden, so merging them would lose their times
        const continuesPrevious = previous && previous.kind === event.kind && !this.selectedChanges.length
          && (event.kind === 'page' || previous.title === title)
        if (!continuesPrevious) {
          rows.push({key: event.id, time: event.time, kind: event.kind, title, lines: [], pages: [], count: 0})
        }
        const row = rows[rows.length - 1]
        row.count++
        if (event.kind === 'page') {
          if (row.pages[row.pages.length - 1] !== title) row.pages.push(title)
        } else if (detail && !row.lines.some(line => line.id === detail.id)) {
          row.lines.push(detail)
        }
      }
      rows.filter(row => row.kind === 'page').forEach(row => {
        row.title = `Viewed ${row.pages.join(' → ')}`
        row.count = 1
      })
      return rows
    },

    eventTitle(event) {
      if (event.kind === 'page') return event.props?.name || event.props?.path || 'page'
      if (event.kind === 'api') return 'API calls'
      return capitalise(event.label)
    },

    // one line saying what the event carried: plain text, or its arguments as name/value pairs
    eventDetail(event) {
      const props = event.props || {}
      if (event.kind === 'api') return {id: event.label, text: event.label}
      if (event.event === 'login') return null
      if (event.event === 'pause deliveries' && !props.from && !props.to) return {id: 'cleared', text: 'pause cleared'}
      const args = Object.entries(props)
        .filter(([key, value]) => !this.isRoutineProp(key, value))
        .map(([key, value]) => ({key, value: /password/i.test(key) ? '••••' : formatValue(value)}))
      if (!args.length) return null
      const mealId = mealIdOf(props)
      args.filter(arg => arg.key === 'meal_id' || (arg.key === 'id' && mealId)).forEach(arg => {
        arg.mealId = String(mealId)
      })
      return {id: JSON.stringify(args), args}
    },

    isRoutineProp(key, value) {
      if (key === 'app') return true
      if (key === 'uid' && value === this.uid) return true
      return false
    },

    formatClient(details) {
      const parts = [`build ${details.buildVersion}`]
      if (details.isApp) parts.push('native app')
      if (details.screenWidth) parts.push(`screen ${details.screenWidth}×${details.screenHeight}`)
      if (details.timezone) parts.push(details.timezone)
      return parts.join(' · ')
    },

    formatUserAgent(userAgentString) {
      if (!userAgentString) return {label: 'Unknown device', icon: 'mdi-help-circle-outline'}
      const UAParser = require('ua-parser-js')
      const result = new UAParser(userAgentString).getResult()

      const browser = result.browser.name ? `${result.browser.name} ${result.browser.major || ''}`.trim() : 'Unknown browser'
      const os = result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : 'unknown OS'
      const deviceIcons = {mobile: 'mdi-cellphone', tablet: 'mdi-tablet'}
      return {label: `${browser} on ${os}`, icon: deviceIcons[result.device.type] || 'mdi-monitor'}
    },

    formatTime(millis, withSeconds) {
      return moment(millis).format(withSeconds ? 'h:mm:ss a' : 'h:mm a')
    },

    formatDuration(millis) {
      const minutes = Math.round(millis / 60000)
      if (minutes < 1) return 'under a minute'
      if (minutes < 60) return `${minutes} min`
      return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '-'
      return moment(toMillis(timestamp)).format('MMM D YYYY, h:mm a')
    },

    plural(count, noun) {
      return `${count} ${noun}${count === 1 ? '' : 's'}`
    },

    getUserProperties(userInfo) {
      const userProps = {}
      const targetKeys = ['user-agent', 'origin', 'build', 'buildVersion', 'platform', 'capacitor', 'referer', 'hostname']
      targetKeys.forEach(key => {
        if (key in userInfo) {
          userProps[key] = userInfo[key]
        }
      })
      return userProps
    },

    formatPropHeader(key) {
      return String(key).replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .trim()
    },

    getLastChanged(key) {
      return this.trackingData?.userInfo?.[`${key}-lastChanged`] || null
    }
  }
}
</script>

<style scoped>
.visit-with-changes {
  border-left: 4px solid var(--v-primary-base);
}

.timeline-row {
  font-size: 13px;
  padding: 2px 0;
}

.timeline-time {
  min-width: 90px;
  color: grey;
  font-variant-numeric: tabular-nums;
}

.timeline-page, .timeline-info, .timeline-api {
  color: grey;
}

.timeline-change .timeline-title {
  font-weight: 500;
}

.timeline-detail {
  font-size: 12px;
  word-break: break-word;
  margin: 2px 0;
}

.timeline-arg {
  display: inline-block;
  margin: 0 6px 2px 0;
  padding: 0 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
}

.timeline-arg-key {
  color: grey;
  margin-right: 4px;
}

.timeline-arg-value {
  font-weight: 600;
}
</style>
